import { useRouter } from 'next/router'
import { useState, useEffect, useMemo } from 'react'
import { CheckoutContextProvider } from 'utils/checkout'
import useSWR from 'swr'
import Checkout from 'components/checkout/checkout'
import Image from 'components/image/image'
import Panel from 'components/panel/panel'
import Heading from 'components/heading/heading'
import Meta from 'components/meta/meta'
import Infoblock from 'components/infoblock/infoblock'
import Search from 'components/input/search/search'
import Textarea from 'components/input/textarea/textarea'
import Input from 'components/input/input/input'
import InputGroup from 'components/input-group/input-group'
import OutsideDelivery from 'components/outside-delivery/outside-delivery'
import Btn from 'components/btn/btn'
import cities from 'utils/data/cities'
import { cn, parseFormEvent, getRoute } from 'utils/helpers'
import { getUrl } from 'utils/api'
import styles from 'styles/page/pirkimas-pristatymas.module.scss'
import useAppState from 'utils/state'
import useCheckout from 'utils/checkout'
import { trackFillInformation, trackSelectShipping } from 'utils/analytics/events'
import Radio from 'components/input/radio/radio'
import { useModal } from 'components/modals/provider'

const deliveryTypes = [
    { name: 'Lietuvoje', value: 'lt' },
    { name: 'Kitoje šalyje', value: 'outside' }
]

function Delivery() {
    const { showMessage } = useModal()
    const { cart, updateCartDetails, updateApiBasket, user, delivery: stateDelivery } = useAppState()
    const { setup, updateSetup, setupLoading } = useCheckout()
    const [info, setInfo] = useState(cart.details.delivery)
    const router = useRouter()

    const outsideDeliveries =
        setup.availableDeliveries && setup.availableDeliveries.filter(del => ['eu', 'global'].includes(del.value))
    const deliveryRadio = outsideDeliveries && outsideDeliveries.length
    const deliveryType = info.delivery_type
    const availableDeliveries = setup ? setup.availableDeliveries : null

    useEffect(() => {
        if (!setupLoading) {
            const { delivery_type } = cart.details.delivery
            const defaultType = delivery_type ? delivery_type : stateDelivery === 'lt' ? 'lt' : 'global'
            const deliveryType = defaultType && deliveryRadio ? defaultType : 'lt'
            handleChange({ name: 'delivery_type', value: deliveryType })
            setInfo({ ...cart.details.delivery, delivery_type: deliveryType })
            trackFillInformation(cart.items)
        }
    }, [setupLoading])

    // vars
    const { delivery } = info
    const isTerminals = !setupLoading
        ? !['from_address', 'courier', 'eu', 'global', 'bus_station'].includes(delivery) && delivery
        : null
    const city = !setupLoading ? isTerminals && info[`${delivery}_city`] : null
    const desc = 'Prekių pristatymo arba atsiėmimo pasirinkimas ir prekes atsiimančio asmens informacijos pateikimas.'

    // load info from api
    const { data: deliveryCities } = useSWR(!setupLoading && isTerminals ? getUrl(`terminals/${delivery}`) : null)
    const { data: deliveryTerminals } = useSWR(!setupLoading && city ? getUrl(`terminals/${delivery}/${city}`) : null)
    const { data: stationList } = useSWR(
        !setupLoading && delivery === 'bus_station' && setup.fromStation
            ? getUrl(`stations/${setup.fromStation}`)
            : null
    )
    // console.log(getUrl(`stations/${setup.fromStation}`))

    const stationOptions = useMemo(() => {
        if (!stationList?.data) return []

        return stationList.data.map(i => ({ name: i.label, value: i.label }))
    }, [stationList])
    // const { data: stationList } = useSWR(
    //     !setupLoading && delivery === 'bus_station' && setup.fromStation
    //         ? `https://www.siuntosautobusais.lt/api.php?action=arrival&leave_id=${setup.fromStation}`
    //         : null
    // )

    // console.log(deliveryCities, deliveryTerminals)

    if (setupLoading) return ''

    const terminalField = `${delivery}_terminal`
    const cityField = `${delivery}_city`

    const handleDeliveryChange = (delivery, delivery_type = 'lt', outside_country = info.outside_country) => {
        setInfo({
            ...info,
            delivery,
            delivery_type,
            outside_country
        })
        updateCartDetails({ ...cart.details, delivery: info })
        updateSetup(delivery)
    }

    const handleChange = e => {
        const { name, value } = parseFormEvent(e)
        setInfo({ ...info, [name]: value })
    }

    console.log(user)

    const handleSubmit = e => {
        e.preventDefault()
        const matching = info.phone.match(/\d/g)
        const validPhone = matching && matching.length > 5
        if (!validPhone) {
            showMessage('Neteisingas telefono numeris.')
        } else {
            updateCartDetails({ ...cart.details, delivery: info, has_delivery: true })
            updateApiBasket(cart.items, user ? user.email : cart.details.information.email, false)
            trackSelectShipping(cart.items)
            router.push(getRoute('checkout-payment'))
        }
    }

    const handleRadioChange = e => {
        const { value } = parseFormEvent(e)
        if (value == 'lt') {
            handleDeliveryChange(availableDeliveries[0].value)
        } else {
            handleChange(e)
        }
    }

    let mapLink
    if (setup.availableDeliveries && info.delivery) {
        mapLink = setup.availableDeliveries.find(d => d.value == info.delivery)?.map
    }

    return (
        <Checkout summary>
            <Meta title="Pristatymas" description={desc} />
            <Panel className="mb120" desktop>
                <form onSubmit={handleSubmit}>
                    <div className="mb24">
                        <Heading tag="h2" size="24" className="mb36">
                            Kas atsiims prekes?
                        </Heading>
                        <InputGroup>
                            <Input
                                type="text"
                                name="name"
                                label="Vardas, pavardė"
                                value={info.name}
                                onChange={handleChange}
                                required
                            />
                            <Input
                                type="text"
                                name="phone"
                                label="Telefono numeris"
                                value={info.phone}
                                onChange={handleChange}
                                required
                                info="Jeigu prireiktų susisiekti dėl užsakymo."
                            />
                        </InputGroup>
                    </div>
                    <div className={styles.deliveriesContainer}>
                        <input
                            className={styles.deliveryHiddenRequired}
                            type="text"
                            value={info.delivery}
                            tabIndex={-1}
                            autoComplete="off"
                            inputMode="none"
                            required
                            onChange={() => { }}
                        />
                        {setup.availableDeliveries && (
                            <>
                                <Heading tag="h2" size="24" className="mb36">
                                    Pristatymo būdas
                                </Heading>
                                {!!deliveryRadio && (
                                    <Radio
                                        name="delivery_type"
                                        value={deliveryType}
                                        onChange={handleRadioChange}
                                        options={deliveryTypes}
                                        required
                                    />
                                )}
                                {deliveryType == 'lt' && (
                                    <div className="mb48">
                                        <div className={styles.deliveries}>
                                            {setup.availableDeliveries
                                                .filter(del => !['eu', 'global'].includes(del.value))
                                                .filter(del => del.value != 'bus_station' ? true : false)
                                                .map(option => (
                                                    <div
                                                        key={option.value}
                                                        className={cn(info.delivery == option.value && styles.active)}
                                                    >
                                                        <div onClick={() => handleDeliveryChange(option.value)}>
                                                            <div className={styles.text}>
                                                                {option.name}
                                                                {option.value === 'bus_station' ? (
                                                                    <i>Apmokama atsiimant</i>
                                                                ) : (
                                                                    <b>{option.price}€</b>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <Image
                                                                    src={`/delivery/${option.value}.svg`}
                                                                    alt={option.name}
                                                                    title={option.name}
                                                                    width="50"
                                                                    height="50"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {deliveryType == 'lt' && !['eu', 'global'].includes(info.delivery) && (
                        <>
                            {info.delivery && (
                                <Heading tag="h2" size="24" className="mb36">
                                    Papildoma informacija
                                </Heading>
                            )}
                            {isTerminals && (
                                <InputGroup>
                                    {deliveryCities && (
                                        <Search
                                            label="Miestas"
                                            options={deliveryCities.data}
                                            value={info[cityField]}
                                            name={cityField}
                                            onChange={handleChange}
                                            required
                                        />
                                    )}
                                    {deliveryTerminals && (
                                        <Search
                                            label="Terminalas"
                                            options={deliveryTerminals.data}
                                            value={info[terminalField]}
                                            name={terminalField}
                                            onChange={handleChange}
                                            required
                                        />
                                    )}
                                </InputGroup>
                            )}
                            {delivery === 'bus_station' && (
                                <>
                                    <InputGroup>
                                        <Search
                                            name="bus_station"
                                            label="Atsiėmimo stotis"
                                            options={stationOptions || []}
                                            value={info.bus_station}
                                            onChange={handleChange}
                                            required
                                        />
                                    </InputGroup>
                                </>
                            )}
                            {delivery === 'courier' && (
                                <>
                                    <InputGroup>
                                        <Search
                                            name="courier_city"
                                            label="Miestas"
                                            options={cities || {}}
                                            value={info.courier_city}
                                            onChange={handleChange}
                                            required
                                        />
                                        <Input
                                            type="text"
                                            name="courier_zip"
                                            label="Pašto kodas"
                                            value={info.courier_zip}
                                            onChange={handleChange}
                                            required
                                        />
                                    </InputGroup>
                                    <Input
                                        type="text"
                                        name="courier_address"
                                        label="Adresas"
                                        value={info.courier_address}
                                        onChange={handleChange}
                                        required
                                    />
                                    <Textarea
                                        name="courier_notes"
                                        label="Informacija kurjeriui"
                                        value={info.courier_notes}
                                        onChange={handleChange}
                                    />
                                </>
                            )}
                            {mapLink && (
                                <div className={cn(styles.mapLink, 'mb30')}>
                                    <a target="_blank" href={mapLink} rel="noreferrer">
                                        Atsiėmimo vietų žemėlapis
                                    </a>
                                </div>
                            )}
                            {setup.availableDeliveries && info.delivery && (
                                <Infoblock type="warning" className="mb48">
                                    {setup.availableDeliveries.find(d => d.value == info.delivery)?.info}
                                    {info.delivery == 'from_address' ? setup.fromAddress : ''}
                                </Infoblock>
                            )}
                        </>
                    )}
                    {deliveryType != 'lt' && (
                        <OutsideDelivery
                            handleDeliveryChange={handleDeliveryChange}
                            availableDeliveries={availableDeliveries}
                            info={info}
                            setInfo={setInfo}
                        />
                    )}
                    <Btn block style="yellow" type="submit">
                        Toliau: APMOKĖJIMAS
                    </Btn>
                </form>
            </Panel>
        </Checkout>
    )
}

export default function Page() {
    return (
        <CheckoutContextProvider>
            <Delivery />
        </CheckoutContextProvider>
    )
}
